import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { OnboardingBanner } from '../components/kyc/OnboardingBanner';
import {
  Wallet, Copy, Check, ArrowRight, ArrowLeft, Upload, Loader2,
  AlertTriangle, CheckCircle2, Clock, XCircle, Hash, FileText, Eye,
  Coins, Banknote, Landmark, ShieldCheck, Info, LifeBuoy, ArrowUpRight,
  Lock,
} from 'lucide-react';

const STATUS_META = {
  created: { label: 'Created', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', icon: Clock },
  proof_submitted: { label: 'Pending Verification', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: Clock },
  pending_verification: { label: 'Pending Verification', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: Clock },
  under_review: { label: 'Under Review', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', icon: Clock },
  verified: { label: 'Verified', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', icon: CheckCircle2 },
  credited: { label: 'Credited', cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300', icon: CheckCircle2 },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', icon: XCircle },
  expired: { label: 'Expired', cls: 'bg-gray-100 text-gray-500', icon: XCircle },
};

const METHOD_LABELS = {
  usdt: 'Crypto Deposit',
  cash_deposit: 'Cash Deposit',
  bank_transfer: 'Bank Transfer',
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.created;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>
      <Icon className="w-3 h-3" />{m.label}
    </span>
  );
};

export default function WalletRechargePage() {
  const { api, merchant } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState('select'); // 'select' | 'crypto'
  const [methods, setMethods] = useState([]);
  const [supportEmail, setSupportEmail] = useState('support@awadhpay.com');
  const [settings, setSettings] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // crypto flow state
  const [step, setStep] = useState(1);
  const [usdtAmount, setUsdtAmount] = useState('');
  const [funding, setFunding] = useState(null);
  const [txHash, setTxHash] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [success, setSuccess] = useState(null);
  const [detail, setDetail] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [m, s, w, h] = await Promise.all([
        api.get('/funding/methods').catch(() => ({ data: { methods: [] } })),
        api.get('/funding/settings/usdt').catch(() => ({ data: null })),
        api.get('/wallet/summary').catch(() => ({ data: null })),
        api.get('/funding/requests').catch(() => ({ data: { requests: [] } })),
      ]);
      setMethods(m.data?.methods || []);
      if (m.data?.support_email) setSupportEmail(m.data.support_email);
      setSettings(s.data);
      setWallet(w.data);
      setHistory(h.data?.requests || []);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const cryptoMethod = methods.find((x) => x.id === 'crypto') || {};
  const cashMethod = methods.find((x) => x.id === 'cash') || {};
  const bankMethod = methods.find((x) => x.id === 'bank_transfer') || {};

  const rate = settings?.exchange_rate || cryptoMethod.exchange_rate || 0;
  const min = settings?.min_amount || cryptoMethod.min_amount || 0;
  const max = settings?.max_amount || cryptoMethod.max_amount || 0;
  const address = funding?.admin_wallet_address || settings?.wallet_address;
  const amt = parseFloat(usdtAmount) || 0;
  const grossInr = amt * rate;

  const amountError = (() => {
    if (!usdtAmount) return null;
    if (amt <= 0) return 'Enter a valid amount';
    if (min && amt < min) return `Minimum is ${min} USDT`;
    if (max && amt > max) return `Maximum is ${max} USDT`;
    return null;
  })();

  const contactSupport = () => {
    window.location.href = `mailto:${supportEmail}?subject=AwadhPay%20Wallet%20Recharge%20Support`;
  };

  const unavailableToast = (name) => {
    toast.error(`${name} Unavailable`, {
      description: 'This function is not available now for you. Kindly contact support.',
      action: { label: 'Contact Support', onClick: contactSupport },
    });
  };

  const copyAddr = () => {
    navigator.clipboard.writeText(address || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
    toast.success('Address copied');
  };

  const createRequest = async () => {
    if (!amt || amountError) return;
    setBusy(true);
    try {
      const res = await api.post('/funding/request', { method: 'usdt', usdt_amount: amt });
      setFunding(res.data);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create funding request');
    } finally {
      setBusy(false);
    }
  };

  const submitProof = async () => {
    if (busy) return; // prevent double submission
    if (!txHash.trim() && !file) {
      toast.error('Provide a transaction hash or upload payment proof');
      return;
    }
    const fundingId = funding?.funding_id;
    if (!fundingId) {
      toast.error('Funding reference missing. Please restart the recharge.');
      return;
    }
    setBusy(true);
    try {
      let proofUrl = null;
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        const up = await api.post(`/funding/${fundingId}/proof/upload`, fd);
        proofUrl = up.data.proof_url;
      }
      await api.post(`/funding/${fundingId}/proof`, {
        transaction_hash: txHash.trim() || null,
        proof_url: proofUrl,
      });
      // Navigate to the dedicated, refresh-safe success page (driven by funding_id)
      navigate(`/dashboard/wallet/recharge/success?funding_id=${encodeURIComponent(fundingId)}`);
    } catch (e) {
      // Network/timeout: verify whether the request was actually submitted before letting the user retry
      if (!e.response) {
        try {
          const check = await api.get(`/funding/requests/${fundingId}`);
          const st = check.data?.status;
          if (st && ['pending_verification', 'under_review', 'verified', 'approved', 'credited'].includes(st)) {
            navigate(`/dashboard/wallet/recharge/success?funding_id=${encodeURIComponent(fundingId)}`);
            return;
          }
        } catch { /* fall through to error toast */ }
        toast.error('We could not confirm the submission status. Please check Funding History before retrying.');
      } else {
        toast.error(e.response?.data?.detail || 'Submission failed. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const viewProof = async (url) => {
    try {
      const path = url.replace(/^\/api/, '');
      const res = await api.get(path, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank');
    } catch {
      toast.error('Could not open proof');
    }
  };

  const startCrypto = () => {
    if (!cryptoMethod.available) {
      unavailableToast('Crypto Deposit');
      return;
    }
    setStep(1);
    setView('crypto');
    setSuccess(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const kycDone = merchant?.kyc_status === 'approved';
  const balanceCard = (
    <Card className="border-blue-500/20 bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-600/20 min-w-[220px]" data-testid="rp-balance-card">
      <CardContent className="py-4 px-5">
        <div className="flex items-center gap-2 text-blue-100 text-xs mb-1">
          <Wallet className="w-4 h-4" />Available Balance
        </div>
        <p className="text-2xl font-bold font-mono tabular-nums" data-testid="rp-balance">{fmtINR(wallet?.balance?.available)}</p>
        {wallet?.balance?.pending > 0 && (
          <p className="text-xs text-blue-100 mt-1" data-testid="rp-pending">Pending {fmtINR(wallet?.balance?.pending)}</p>
        )}
      </CardContent>
    </Card>
  );

  const steps = ['Amount', 'Transfer', 'Verification'];

  return (
    <div className="space-y-8 max-w-6xl" data-testid="recharge-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900 dark:text-white">Recharge Wallet</h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            {view === 'crypto'
              ? 'Add funds using USDT (TRC20). Funds are credited after verification.'
              : 'Choose a payment method to add funds to your AwadhPay wallet.'}
          </p>
        </div>
        {balanceCard}
      </div>

      <OnboardingBanner testIdPrefix="recharge-onboarding" />

      {/* Success banner */}
      {success && (
        <Card className="border-green-500/40 bg-green-500/5" data-testid="submit-success">
          <CardContent className="py-4 flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold">Payment Submitted</p>
              <p className="text-sm text-muted-foreground">
                Funding <span className="font-mono">{success.funding_id}</span> — {success.usdt_amount} USDT (expected {fmtINR(success.net_credit)}) is awaiting verification by AwadhPay. Your balance updates after approval.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSuccess(null)}>Dismiss</Button>
          </CardContent>
        </Card>
      )}

      {/* ============ METHOD SELECTION VIEW ============ */}
      {view === 'select' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="method-cards">
            {/* CRYPTO */}
            <Card
              className="relative flex flex-col border-2 border-blue-500/60 bg-gradient-to-b from-blue-50/60 to-white dark:from-blue-950/20 dark:to-card shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              data-testid="method-card-crypto"
            >
              <span className="absolute top-4 right-4 text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-blue-600 text-white" data-testid="crypto-badge">Available</span>
              <CardContent className="p-6 flex flex-col flex-1">
                <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center mb-4 shadow-lg shadow-blue-600/30">
                  <Coins className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-heading font-bold text-slate-900 dark:text-white">Crypto Deposit</h3>
                <p className="text-sm text-muted-foreground mt-0.5">Add funds using USDT (TRC20)</p>

                <ul className="mt-4 space-y-2 text-sm flex-1">
                  {['USDT / TRC20', 'Secure wallet funding', 'Live exchange rate', 'Admin verification', 'Wallet credit after approval'].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />{f}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={startCrypto}
                  disabled={!cryptoMethod.available}
                  className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="proceed-crypto-btn"
                >
                  {cryptoMethod.available ? <>Proceed with Crypto <ArrowRight className="w-4 h-4 ml-2" /></> : 'Currently Unavailable'}
                </Button>
              </CardContent>
            </Card>

            {/* CASH */}
            <Card
              className="relative flex flex-col border border-border bg-muted/30 opacity-95 cursor-pointer hover:shadow-md transition-all duration-300"
              onClick={() => unavailableToast('Cash Deposit')}
              data-testid="method-card-cash"
            >
              <span className="absolute top-4 right-4 text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">Not Available</span>
              <CardContent className="p-6 flex flex-col flex-1">
                <div className="w-12 h-12 rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-300 flex items-center justify-center mb-4">
                  <Banknote className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-heading font-bold text-slate-700 dark:text-slate-300">Cash Deposit</h3>
                <p className="text-sm text-muted-foreground mt-0.5">Add funds by cash deposit</p>

                <div className="mt-4 rounded-lg border border-amber-400/50 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-700 dark:text-amber-300 flex gap-2 flex-1">
                  <Info className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>This function is not available now for you. Kindly contact support.</span>
                </div>

                <Button variant="secondary" disabled className="mt-6 w-full cursor-not-allowed" data-testid="cash-disabled-btn">
                  <Lock className="w-4 h-4 mr-2" />Not Available
                </Button>
              </CardContent>
            </Card>

            {/* BANK TRANSFER */}
            <Card
              className="relative flex flex-col border border-border bg-muted/30 opacity-95 cursor-pointer hover:shadow-md transition-all duration-300"
              onClick={() => unavailableToast('Bank Transfer')}
              data-testid="method-card-bank"
            >
              <span className="absolute top-4 right-4 text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">Not Available</span>
              <CardContent className="p-6 flex flex-col flex-1">
                <div className="w-12 h-12 rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-300 flex items-center justify-center mb-4">
                  <Landmark className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-heading font-bold text-slate-700 dark:text-slate-300">Bank Transfer</h3>
                <p className="text-sm text-muted-foreground mt-0.5">Add funds via bank transfer</p>

                <div className="mt-4 rounded-lg border border-amber-400/50 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-700 dark:text-amber-300 flex gap-2 flex-1">
                  <Info className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>This function is not available now for you. Kindly contact support.</span>
                </div>

                <Button variant="secondary" disabled className="mt-6 w-full cursor-not-allowed" data-testid="bank-disabled-btn">
                  <Lock className="w-4 h-4 mr-2" />Not Available
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Info panel */}
          <Card data-testid="recharge-info">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-600" />Wallet Recharge Information</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Currently available funding method: <span className="font-medium text-foreground">Crypto Deposit (USDT - TRC20)</span>.</li>
                <li>• Wallet balance is credited only after funding verification and approval.</li>
                <li>• USDT exchange rate is controlled by AwadhPay and may change over time{rate ? <> (current: <span className="font-medium text-foreground">₹{rate} / USDT</span>)</> : ''}.</li>
                <li>• Always send USDT using the <span className="font-medium text-foreground">TRC20 network</span> to the address displayed during the active funding flow.</li>
                {(min || max) ? <li>• Crypto deposit limits: <span className="font-medium text-foreground">Min {min} USDT · Max {max} USDT</span>.</li> : null}
                <li>• For Cash Deposit or Bank Transfer availability, contact AwadhPay Support.</li>
              </ul>
            </CardContent>
          </Card>

          {/* Support card */}
          <Card className="border-blue-500/20 bg-blue-50/40 dark:bg-blue-950/10" data-testid="support-card">
            <CardContent className="py-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0">
                <LifeBuoy className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900 dark:text-white">Need help with your wallet?</p>
                <p className="text-sm text-muted-foreground">Our support team can help you with recharge and funding questions.</p>
              </div>
              <Button variant="outline" className="border-blue-500/40" onClick={contactSupport} data-testid="contact-support-btn">
                <LifeBuoy className="w-4 h-4 mr-2" />Contact Support
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      {/* ============ CRYPTO FLOW VIEW ============ */}
      {view === 'crypto' && (
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between px-6 py-3 border-b bg-muted/30">
            <Button variant="ghost" size="sm" onClick={() => { setView('select'); setStep(1); }} data-testid="crypto-back-select">
              <ArrowLeft className="w-4 h-4 mr-2" />All methods
            </Button>
            <span className="text-xs font-medium text-blue-600 flex items-center gap-1.5"><Coins className="w-4 h-4" />Crypto Deposit · USDT (TRC20)</span>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2 px-6 py-4 border-b bg-muted/20">
            {steps.map((label, i) => {
              const n = i + 1;
              const active = step === n;
              const done = step > n;
              return (
                <React.Fragment key={label}>
                  <div className="flex items-center gap-2" data-testid={`step-indicator-${n}`}>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                      done ? 'bg-green-500 text-white' : active ? 'bg-blue-600 text-white' : 'bg-muted text-muted-foreground'
                    }`}>
                      {done ? <Check className="w-4 h-4" /> : n}
                    </span>
                    <span className={`text-sm font-medium ${active ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
                  </div>
                  {n < steps.length && <div className="flex-1 h-px bg-border" />}
                </React.Fragment>
              );
            })}
          </div>

          <CardContent className="p-6">
            {/* STEP 1 */}
            {step === 1 && (
              <div className="space-y-5 max-w-lg">
                <div>
                  <Label htmlFor="usdt">Enter USDT Amount</Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="usdt" type="number" min="0" step="0.01" placeholder="0.00"
                      value={usdtAmount} onChange={(e) => setUsdtAmount(e.target.value)}
                      className="text-lg font-mono pr-16" data-testid="rp-amount-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">USDT</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Current Rate: <span className="font-medium text-foreground">₹{rate} / USDT</span> · Min {min} · Max {max} USDT
                  </p>
                  {amountError && <p className="text-xs text-red-500 mt-1" data-testid="rp-amount-error">{amountError}</p>}
                </div>

                <div className="rounded-lg border bg-muted/30 p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">USDT Amount</span><span className="font-mono">{amt || 0} USDT</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Exchange Rate</span><span className="font-mono">₹{rate}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Gross Value</span><span className="font-mono">{fmtINR(grossInr)}</span></div>
                  <div className="flex justify-between border-t pt-2 font-semibold"><span>Net Credit</span><span className="font-mono text-blue-600" data-testid="rp-net-credit">{fmtINR(grossInr)}</span></div>
                </div>

                <Button onClick={createRequest} disabled={!amt || !!amountError || busy} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="rp-continue-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Continue to Payment <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {/* STEP 2 */}
            {step === 2 && funding && (
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Network</p>
                    <p className="font-semibold">TRON — TRC20</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Amount to send</p>
                    <p className="text-xl font-bold font-mono">{funding.usdt_amount} USDT</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Receiving Address</p>
                    <div className="flex items-center gap-2 rounded-lg border p-2.5 bg-muted/30">
                      <code className="text-xs break-all flex-1" data-testid="rp-address">{address}</code>
                      <Button size="icon" variant="ghost" onClick={copyAddr} data-testid="rp-copy-btn">
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-300 flex gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>Send USDT only via the <b>TRC20</b> network to this address. Sending through another network may result in permanent loss of funds.</span>
                  </div>
                  <div className="rounded-lg border bg-muted/30 p-4 space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Funding ID</span><span className="font-mono">{funding.funding_id}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">USDT Amount</span><span className="font-mono">{funding.usdt_amount} USDT</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Rate</span><span className="font-mono">₹{funding.exchange_rate} / USDT</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Expected Credit</span><span className="font-mono text-blue-600">{fmtINR(funding.net_credit)}</span></div>
                  </div>
                </div>
                <div className="flex flex-col items-center justify-center gap-3">
                  <div className="p-4 bg-white rounded-xl border" data-testid="rp-qr">
                    <QRCodeSVG value={address || ''} size={190} level="M" includeMargin />
                  </div>
                  <p className="text-xs text-muted-foreground">Scan to send USDT</p>
                </div>
                <div className="md:col-span-2 flex gap-3">
                  <Button variant="outline" onClick={() => setStep(1)} data-testid="rp-back-btn"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                  <Button onClick={() => setStep(3)} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="rp-transferred-btn">I've completed the transfer <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </div>
              </div>
            )}

            {/* STEP 3 */}
            {step === 3 && funding && (
              <div className="space-y-5 max-w-lg">
                <div>
                  <h3 className="font-semibold">Verify Your Transfer</h3>
                  <p className="text-sm text-muted-foreground">Provide your transaction hash and/or upload payment proof so our team can verify your payment.</p>
                </div>

                <div>
                  <Label htmlFor="hash" className="flex items-center gap-1.5"><Hash className="w-3.5 h-3.5" />TRC20 Transaction Hash</Label>
                  <Input id="hash" placeholder="64-character transaction hash" value={txHash}
                    onChange={(e) => setTxHash(e.target.value)} className="mt-1.5 font-mono text-sm" data-testid="rp-hash-input" />
                </div>

                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <div className="flex-1 h-px bg-border" /> AND / OR <div className="flex-1 h-px bg-border" />
                </div>

                <div>
                  <Label className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />Payment Proof (PNG/JPG/PDF, ≤5MB)</Label>
                  <label className="mt-1.5 flex items-center gap-2 rounded-lg border border-dashed p-3 cursor-pointer hover:bg-accent transition-colors" data-testid="rp-file-label">
                    <Upload className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{file ? file.name : 'Choose a file to upload'}</span>
                    <input type="file" accept=".png,.jpg,.jpeg,.pdf" className="hidden"
                      onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="rp-file-input" />
                  </label>
                </div>

                <div className="rounded-lg border bg-muted/30 p-4 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Funding ID</span><span className="font-mono">{funding.funding_id}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Expected Credit</span><span className="font-mono text-blue-600">{fmtINR(funding.net_credit)}</span></div>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                  <Button onClick={submitProof} disabled={busy || (!txHash.trim() && !file)} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="rp-submit-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Submit for Verification
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Funding History */}
      <Card data-testid="funding-history">
        <CardHeader><CardTitle className="text-lg font-heading">Recent Funding Activity</CardTitle></CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No funding requests yet.</p>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b text-muted-foreground text-left">
                    <th className="py-2 px-3">Funding ID</th>
                    <th className="py-2 px-3">Method</th>
                    <th className="py-2 px-3">Amount</th>
                    <th className="py-2 px-3">Rate</th>
                    <th className="py-2 px-3">INR Credit</th>
                    <th className="py-2 px-3">Date</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((f) => (
                    <tr key={f.funding_id} className="border-b last:border-0 hover:bg-accent/40" data-testid={`history-row-${f.funding_id}`}>
                      <td className="py-2 px-3 font-mono">{f.funding_id}</td>
                      <td className="py-2 px-3">{METHOD_LABELS[f.method] || f.method}</td>
                      <td className="py-2 px-3 font-mono">{f.usdt_amount ? `${f.usdt_amount} USDT` : fmtINR(f.inr_amount)}</td>
                      <td className="py-2 px-3 font-mono">{f.exchange_rate ? `₹${f.exchange_rate}` : '—'}</td>
                      <td className="py-2 px-3 font-mono">{fmtINR(f.net_credit)}</td>
                      <td className="py-2 px-3 text-xs text-muted-foreground whitespace-nowrap">{f.created_at ? new Date(f.created_at).toLocaleDateString('en-IN') : '—'}</td>
                      <td className="py-2 px-3"><StatusBadge status={f.status} /></td>
                      <td className="py-2 px-3"><Button size="sm" variant="ghost" onClick={() => setDetail(f)} data-testid={`history-view-${f.funding_id}`}>View</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="history-detail">
          <DialogHeader><DialogTitle>Funding {detail?.funding_id}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><StatusBadge status={detail.status} /></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Method</span><span>{METHOD_LABELS[detail.method] || detail.method}</span></div>
              {detail.usdt_amount ? <div className="flex justify-between"><span className="text-muted-foreground">USDT Amount</span><span className="font-mono">{detail.usdt_amount} USDT</span></div> : null}
              {detail.exchange_rate ? <div className="flex justify-between"><span className="text-muted-foreground">Exchange Rate</span><span className="font-mono">₹{detail.exchange_rate}</span></div> : null}
              <div className="flex justify-between"><span className="text-muted-foreground">INR Credit</span><span className="font-mono text-blue-600">{fmtINR(detail.net_credit)}</span></div>
              {detail.network && <div className="flex justify-between"><span className="text-muted-foreground">Network</span><span>{detail.network}</span></div>}
              {detail.transaction_hash && <div className="flex justify-between gap-4"><span className="text-muted-foreground">TX Hash</span><span className="font-mono text-xs break-all text-right">{detail.transaction_hash}</span></div>}
              {detail.rejection_reason && (
                <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-2.5 text-red-700 dark:text-red-300 text-xs">
                  <b>Rejection reason:</b> {detail.rejection_reason}
                </div>
              )}
              {detail.proof_url && (
                <Button size="sm" variant="outline" onClick={() => viewProof(detail.proof_url)} data-testid="history-view-proof">
                  <Eye className="w-4 h-4 mr-2" />View Payment Proof
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
